import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { compose } from 'redux';
import debounce from 'lodash/debounce';
import {
  InputAdornment,
  withStyles,
  TextField,
  Button,
  IconButton,
} from '@material-ui/core';
import { H1 } from 'components/Ui';
import { pages } from 'containers/Landing/constants';
import withContentful from 'containers/Landing/withContenfull';
import ContentfulFields from 'components/ContentfulFields';
import { SearchIcon, CloseIconSvg } from 'components/Ui/CustomIcons';
import Typography from '@material-ui/core/Typography';

import ContentfulClient from 'utils/ContentfulClient';
import { searchFAQ } from 'utils/contentfulUtils';
import { getDomainByHost } from 'utils/domainHelper';
import { translateData } from 'utils/translation';
import { isEmpty, isNil } from 'lodash';
import { isSsr } from 'utils/ssrHelper';

const contentfulClient = new ContentfulClient();
const domain = getDomainByHost(16);
// todo - ca n this use styledComponents
const styles = (theme) => ({
  inputRoot: {
    width: '70%',
    height: '75px',
    borderRadius: '37px',
    borderColor: theme.colors.white,
    backgroundColor: theme.colors.white,
    '&$focused': {
      backgroundColor: theme.colors.white,
    },
    '&:hover': {
      backgroundColor: theme.colors.white,
    },
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      height: '60px',
    },
  },
  filled: {
    height: '75px',
    backgroundColor: 'transparent',
    '&$focused': {
      backgroundColor: 'transparent',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  focused: {
    backgroundColor: theme.colors.white,
  },
  input: {
    padding: '0 0 0 20px',
  },
  adornedStart: {
    paddingLeft: '26px',
  },
  faqHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.colors.primaryBlue,
    paddingTop: '45px',
    paddingBottom: '35px',
  },
  title: {
    color: theme.colors.white,
    fontSize: '3.375rem',
    fontWeight: 900,
    lineHeight: '3.375rem',
    marginTop: 0,
    marginBottom: '20px',
  },
  faqContentWrapper: {
    backgroundColor: theme.colors.whiteGrey,
  },
  faqContent: {
    paddingBottom: '68px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    tabContentContainer: {
      backgroundColor: theme.colors.whiteGrey,
    },
  },
  tabContentContainer: {
    backgroundColor: theme.colors.whiteGrey,
  },
  positionStart: {
    marginTop: '0px !important',
  },
  positionEnd: {
    marginRight: '20px',
  },
  scrollToTop: {
    display: 'block',
    color: theme.colors.primaryDarkBlue,
    fontSize: '1.5rem',
    fontWeight: 900,
    lineHeight: '1.5rem',
    margin: '0 auto',
    textTransform: 'none',
    border: '3px solid transparent',
    '&:focus, &:hover': {
      color: `${theme.colors.hoverColor} !important`,
      border: `3px solid ${theme.colors.hoverBorder}`,
    },
  },
  iconRoot: {
    padding: 0,
  },
  emptyList: {
    paddingTop: '20px',
    marginTop: '40px',
    textAlign: 'center',
  },
});

class PrimoFAQ extends PureComponent {
  state = {
    searchValue: '',
    searchResult: null,
  };

  search = debounce(async () => {
    const result = await searchFAQ(
      domain,
      contentfulClient,
      this.state.searchValue,
    );
    const ids = result.items.map((item) => item.sys.id);

    this.setState({ searchResult: ids });
  }, 300);

  handleChange = ({ target: { value } }) => {
    this.setState({ searchValue: value });

    this.search();
  };

  setFocusToSearchField = () => {
    const searchField = document.querySelector('#searchField');
    searchField.focus();
  };

  handleClean = () => {
    this.setState({ searchValue: '', searchResult: null });
    this.setFocusToSearchField();
  };

  handleScrollToTop = () => {
    if (!isSsr()) {
      window.scrollTo(0, 0);
      this.setFocusToSearchField();
    }
  };

  render() {
    const {
      site: {
        components: { page },
      },
      classes,
    } = this.props;
    const { searchResult, searchValue } = this.state;

    return (
      <Fragment>
        <div className={classes.faqHeader}>
          <H1 className={classes.title}>FAQs</H1>
          <TextField
            id="searchField"
            classes={{ root: classes.inputRoot }}
            InputProps={{
              inputProps: { 'aria-label': 'Search' },
              disableUnderline: true,
              startAdornment: (
                <InputAdornment
                  classes={{ positionStart: classes.positionStart }}
                  position="start"
                >
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment:
                searchValue === '' ? null : (
                  <InputAdornment
                    classes={{ positionEnd: classes.positionEnd }}
                    position="end"
                  >
                    <IconButton
                      classes={{ root: classes.iconRoot }}
                      onClick={this.handleClean}
                      aria-label={'Close search'}
                    >
                      <CloseIconSvg />
                    </IconButton>
                  </InputAdornment>
                ),
              placeholder: translateData('Search'),
              classes: {
                root: classes.filled,
                focused: classes.focused,
                input: classes.input,
                adornedStart: classes.adornedStart,
              },
            }}
            variant="filled"
            onChange={this.handleChange}
            value={searchValue}
          />
        </div>
        <div className={classes.faqContentWrapper} aria-live="polite">
          <div className={classes.faqContent}>
            {page &&
              page.contentModules.map((banner) => (
                <ContentfulFields
                  type={get(banner, 'sys.contentType.sys.id')}
                  document={banner}
                  key={banner.sys.id}
                  searchResult={searchResult}
                  classes={{ tabContentContainer: classes.tabContentContainer }}
                />
              ))}

            {isNil(searchResult) ||
              (isEmpty(searchResult) && (
                <Typography
                  variant="h3"
                  align="center"
                  gutterBottom
                  gutterTop
                  className={classes.emptyList}
                >
                  {translateData('No search results found')}
                </Typography>
              ))}

            {(searchValue === '' ||
              (searchResult !== null && searchResult.length !== 0)) && (
              <Button
                classes={{
                  root: classes.scrollToTop,
                }}
                onClick={this.handleScrollToTop}
              >
                {translateData('Back to top')}
              </Button>
            )}
          </div>
        </div>
      </Fragment>
    );
  }
}

PrimoFAQ.propTypes = {
  site: PropTypes.object,
  classes: PropTypes.object,
};

export default compose(withStyles(styles), withContentful(pages.faq))(PrimoFAQ);
